import type { OptionDB } from "@project/emerge";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { ROLLER_TYPE } from "../enum/ROLLER_TYPE";
import { VariableOptionType } from "../enum/VariableOptionType";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type {
  CreeperRollerType,
  RollerProperties,
  RollerType,
} from "../interfaces/RollerBoardProperties";

/** Non-enumerable markers on legacy exterior creeper (bundle) for A/B catalog rows */
const HYDRATED_WHEEL_A = "__hydratedWheelA";
const HYDRATED_WHEEL_B = "__hydratedWheelB";

function isCreeperBundleMeshes(roller: CreeperRollerType): boolean {
  const m = roller.meshes as Record<string, unknown> | undefined;
  if (!m || typeof m !== "object") return false;
  return "rollerA" in m && "rollerB" in m;
}

function isSingleWheelCreeperCatalogEntry(r: RollerType): boolean {
  if (r.rollerType !== ROLLER_TYPE.CREEPER) return false;
  const m = r.meshes as Record<string, unknown> | undefined;
  if (!m || typeof m !== "object") return false;
  const keys = Object.keys(m);
  return keys.length === 1 && keys[0] === "default";
}

function resolveSingleCasterFromNested(
  nested: RollerProperties | undefined,
  optionDb: OptionDB,
): CreeperRollerType | undefined {
  if (!nested?.name) return undefined;
  const key = nested.name.replaceAll(/\s/g, "");
  const all =
    optionDb.getAllOfKind<RollerType>(LTCaseUserOption.RollerType) ?? [];
  for (const r of all) {
    if (!isSingleWheelCreeperCatalogEntry(r)) continue;
    const nameMatch = r.name?.replaceAll(/\s/g, "") === key;
    const aliasMatch = r.aliases?.some((a) => a.replaceAll(/\s/g, "") === key);
    if (nameMatch || aliasMatch) {
      return r as CreeperRollerType;
    }
  }
  return undefined;
}

function resolveCreeperSlotToCatalogSingle(
  roller: CreeperRollerType,
  wheel: "A" | "B",
  optionDb: OptionDB,
): CreeperRollerType {
  if (!isCreeperBundleMeshes(roller)) return roller;
  const nested = wheel === "A" ? roller.rollerA : roller.rollerB;
  return resolveSingleCasterFromNested(nested, optionDb) ?? roller;
}

function defineHidden(target: object, key: string, value: unknown): void {
  Object.defineProperty(target, key, {
    value,
    enumerable: false,
    configurable: true,
    writable: true,
  });
}

/**
 * After `migrateFlightCaseSettings`, replace imported rollbrett **bundle**
 * creeper data (meshes.rollerA/B, full board JSON) with catalog **single-caster**
 * rows (`meshes.default`, e.g. ROL3470) so downstream code matches freshly built
 * cases. Idempotent for already-clean options.
 *
 * Called from `FlightCaseDefinition` (constructor) so JSON/unit-test/debug
 * imports share one path without resolver logic in `RollerBuildStep`.
 */
export function hydrateImportedCreeperRollers(
  settings: FlightCaseSettings,
  optionDb: OptionDB,
): void {
  const rollers = settings.exteriorOptions?.rollers;
  if (
    rollers &&
    typeof rollers === "object" &&
    (rollers as CreeperRollerType).rollerType === ROLLER_TYPE.CREEPER &&
    isCreeperBundleMeshes(rollers as CreeperRollerType)
  ) {
    const cr = rollers as CreeperRollerType;
    const a = resolveSingleCasterFromNested(cr.rollerA, optionDb);
    const b = resolveSingleCasterFromNested(cr.rollerB, optionDb);
    if (a) defineHidden(cr, HYDRATED_WHEEL_A, a);
    if (b) defineHidden(cr, HYDRATED_WHEEL_B, b);
  }

  for (const opt of settings.variableOptions ?? []) {
    if (opt?.variableOptionType !== VariableOptionType.ROLLER_BOARD) {
      continue;
    }
    const rb = opt;
    if (rb.rollerA && isCreeperBundleMeshes(rb.rollerA)) {
      rb.rollerA = resolveCreeperSlotToCatalogSingle(rb.rollerA, "A", optionDb);
    }
    if (rb.rollerB && isCreeperBundleMeshes(rb.rollerB)) {
      rb.rollerB = resolveCreeperSlotToCatalogSingle(rb.rollerB, "B", optionDb);
    }
  }
}

/**
 * Legacy exterior stores one creeper bundle; synthetic {@link RollerBoardInfos}
 * needs distinct A/B catalog rows. Used only by {@link RollerBuildStep}
 * `createLegacyRollerBoardOption`.
 */
export function getHydratedLegacyCreeperWheel(
  creeper: CreeperRollerType,
  wheel: "A" | "B",
): CreeperRollerType {
  const key = wheel === "A" ? HYDRATED_WHEEL_A : HYDRATED_WHEEL_B;
  const resolved = (creeper as Record<string, unknown>)[key] as
    | CreeperRollerType
    | undefined;
  return resolved ?? creeper;
}
